import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, Loader2Icon, MailIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ChoiceChips } from '../components/ChoiceChips';
import { ACTIVATION_FORM_ENDPOINT, ACTIVATION_SUCCESS_URL, ActivationAnswers, activationMailto, emptyAnswers, featureOptions, heardFromOptions, prescriptionOptions, recommendOptions } from '../data/activation';
import { trackCustom } from '../utils/tracking';
type Status = 'idle' | 'submitting' | 'success' | 'error' | 'not_connected';
const ownerActions = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Join GritVue Crew Rewards',
  body: 'Share GritVue. Get paid.',
  to: '/rewards'
}];
const inputClass = 'mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3 text-[16px] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none';
const labelClass = 'text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400';
export function Activate() {
  const [answers, setAnswers] = useState<ActivationAnswers>(emptyAnswers);
  const [status, setStatus] = useState<Status>('idle');
  const started = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('activation_page_visited');
  }, []);
  const update = (key: keyof ActivationAnswers, value: string) => {
    if (!started.current) {
      started.current = true;
      trackCustom('activation_started');
    }
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    if (status === 'error' || status === 'not_connected') setStatus('idle');
  };
  const canSubmit = answers.orderNumber.trim() !== '' && /\S+@\S+\.\S+/.test(answers.email);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || status === 'submitting') return;
    // No backend yet: say so. Nothing is stored and nothing is faked.
    if (!ACTIVATION_FORM_ENDPOINT) {
      setStatus('not_connected');
      trackCustom('activation_not_connected');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(ACTIVATION_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...answers,
          source: 'gritvue_activate',
          submittedAt: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error(String(response.status));
      trackCustom('activation_completed', {
        feature: answers.feature,
        heard_from: answers.heardFrom,
        recommend: answers.recommend,
        wears_prescription: answers.wearsPrescription
      });
      if (ACTIVATION_SUCCESS_URL) {
        window.location.href = ACTIVATION_SUCCESS_URL;
        return;
      }
      setStatus('success');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch {
      setStatus('error');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1597" data-mp="x07olguarfu5">
      <PageHeader data-id="element-1598" data-mp="s63cyka36jmq" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1599" data-mp="s5l84z9cyx37">
        <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-1600" data-mp="j6gd42x09lvs">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1601" data-mp="j8179rfer2t4">Owner activation</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1602" data-mp="e9pxfgp44saw">
            Activate your
            <br data-id="element-1603" data-mp="49z61nvumtf1" />
            <span className="text-grit-500" data-id="element-1604" data-mp="4aiiip2qcplo">GritVue X1™</span>
          </h1>
          <p className="mt-5 max-w-lg font-display text-xl font-bold uppercase leading-snug text-offwhite" data-id="element-1605" data-mp="23n34vesiuwg8">
            Make it official. Register your X1. Unlock owner resources. Help us build better gear.
          </p>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-steel-300" data-id="element-1606" data-mp="26p7p7q1i36o7">
            Takes about a minute. Your answers tell us who uses the X1 and what to build next.
          </p>
          <Link to="/verify" className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-1607" data-mp="1woyn5vktur0w">
            Verify your production batch
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1608" data-mp="1nvtyjx4tlmee" />
          </Link>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1609" data-mp="1sq824mtmyzu5">
          {status === 'success' ? <motion.div role="status" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1610" data-mp="1e1bswmfufgry">
              <div className="border-2 border-grit-500 bg-grit-500/10 p-6 sm:p-8" data-id="element-1611" data-mp="190eaq9uu2mqg">
                <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase text-grit-500" data-id="element-1612" data-mp="1928o933rzcki">
                  <BadgeCheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-1613" data-mp="ldafsftwle2u" />
                  Your GritVue X1 is registered.
                </p>
                <p className="mt-2 text-[15px] text-steel-200" data-id="element-1614" data-mp="zdl71jz4kuoj">Welcome to GritVue. Thanks for helping us build better gear.</p>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1615" data-mp="19azpfp4lztjh">
                {ownerActions.map(item => <li key={item.title} data-id="element-1616" data-mp="14c4u5npggcih">
                    <Link to={item.to} className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1617" data-mp="14b3knno37y2v">
                      <span data-id="element-1618" data-mp="1s0xudwd416iw">
                        <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1619" data-mp="1s290i2u12qd6">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1620" data-mp="1n0jl6azrvqja">{item.body}</span>
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1621" data-mp="oq1wzv41maip" />
                    </Link>
                  </li>)}
                <li data-id="element-1622" data-mp="tnjzp3rgvti9">
                  <a href="/#rx" className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1623" data-mp="yg3a5ruuy8iu">
                    <span data-id="element-1624" data-mp="18czl0488b6qk">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1625" data-mp="18gdoxgwjjo04">Add RX capability</span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1626" data-mp="1hfh8sn3j87fo">Available now: RX Capability Insert, +$14.95</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1627" data-mp="3e9vkcvtnucel" />
                  </a>
                </li>
              </ul>
            </motion.div> : <form onSubmit={submit} noValidate className="grid gap-6 border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1628" data-mp="3bow6jeasjgbo">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-1629" data-mp="31x05budera38">
                <div data-id="element-1630" data-mp="31xjdijd9ak42">
                  <label htmlFor="act-order" className={labelClass} data-id="element-1631" data-mp="31ux3md2w7sio">Order number</label>
                  <input id="act-order" required value={answers.orderNumber} onChange={e => update('orderNumber', e.target.value)} placeholder="#1001" autoComplete="off" className={inputClass} data-id="element-1632" data-mp="2ww3susezqfm7" />
                </div>
                <div data-id="element-1633" data-mp="3knr9mhijgaar">
                  <label htmlFor="act-email" className={labelClass} data-id="element-1634" data-mp="3kn81fsiox09x">Email</label>
                  <input id="act-email" type="email" required value={answers.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" autoComplete="email" className={inputClass} data-id="element-1635" data-mp="3gmfuj5xaj7gt" />
                </div>
              </div>

              <div className="grid gap-5 border-t border-white/10 pt-6" data-id="element-1636" data-mp="255frbf9en880">
                <div data-id="element-1637" data-mp="863f5gbldkji">
                  <label htmlFor="act-use" className={labelClass} data-id="element-1638" data-mp="85l8dpraznxc">What do you use your X1 for?</label>
                  <input id="act-use" value={answers.useCase} onChange={e => update('useCase', e.target.value)} placeholder="Engine bays, attic runs, night calls..." className={inputClass} data-id="element-1639" data-mp="d70yhw8c8kml" />
                </div>
                <div data-id="element-1640" data-mp="451f4x9up4gh">
                  <label htmlFor="act-trade" className={labelClass} data-id="element-1641" data-mp="ruc8htovfwkl">What&apos;s your profession / trade?</label>
                  <input id="act-trade" value={answers.trade} onChange={e => update('trade', e.target.value)} placeholder="Electrician, mechanic, HVAC..." className={inputClass} data-id="element-1642" data-mp="vvpdk1k53vqg" />
                </div>
                <div data-id="element-1643" data-mp="n3dmqel4669y">
                  <label htmlFor="act-problem" className={labelClass} data-id="element-1644" data-mp="n2ltvbiwimnd">What problem did you most want X1 to solve?</label>
                  <input id="act-problem" value={answers.problem} onChange={e => update('problem', e.target.value)} placeholder="Holding a flashlight in my teeth..." className={inputClass} data-id="element-1645" data-mp="1arn85fef0s0k" />
                </div>
              </div>

              <ChoiceChips legend="Which feature mattered most?" options={featureOptions} value={answers.feature} onChange={v => update('feature', v)} data-id="element-1646" data-mp="1owdtlkbryxip" />
              <ChoiceChips legend="Where did you first hear about GritVue?" options={heardFromOptions} value={answers.heardFrom} onChange={v => update('heardFrom', v)} data-id="element-1647" data-mp="1yn8f2mh75t33" />
              <ChoiceChips legend="Would you recommend X1 to a coworker?" options={recommendOptions} value={answers.recommend} onChange={v => update('recommend', v)} data-id="element-1648" data-mp="15zcm9sur0lbl" />
              <ChoiceChips legend="Do you wear prescription lenses?" options={prescriptionOptions} value={answers.wearsPrescription} onChange={v => update('wearsPrescription', v)} optional data-id="element-1649" data-mp="120n5rreeynnq" />

              <div data-id="element-1650" data-mp="1b1bmitv6z24b">
                <button type="submit" disabled={!canSubmit || status === 'submitting'} className="flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1651" data-mp="2rcyucyukf10s">
                  {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1652" data-mp="1wmnp4singfh7" />}
                  {status === 'submitting' ? 'Activating...' : 'Activate my X1'}
                </button>
                {!canSubmit && <p className="mt-2 text-[12px] text-steel-500" data-id="element-1653" data-mp="1nlfuqlj97yse">Order number and a valid email are required.</p>}

                {status === 'error' && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.03] p-4 text-[14px] text-steel-200" data-id="element-1654" data-mp="1dpuv2u1nralt">
                    Something went wrong and your activation wasn&apos;t saved. Try again, or email support@gritvuegear.com.
                  </p>}
                {status === 'not_connected' && <div role="status" className="mt-4 border border-white/20 bg-white/[0.03] p-4" data-id="element-1655" data-mp="3ghs4sxzjz76v">
                    <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1656" data-mp="3kittwlzra2v4">Online activation is being connected</p>
                    <p className="mt-1 text-[14px] text-steel-300" data-id="element-1657" data-mp="3ubj4lenhfcpe">
                      Your answers have not been saved yet. Send them by email and we&apos;ll register your X1 by hand.
                    </p>
                    <a href={activationMailto(answers)} className="mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-4 py-2.5 font-display text-base font-extrabold uppercase text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950" data-id="element-1658" data-mp="31lkcfadzqzgi">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1659" data-mp="lxah2kzthc43" />
                      Email my activation
                    </a>
                  </div>}
                <p className="mt-4 text-[12px] leading-relaxed text-steel-500" data-id="element-1660" data-mp="14kfsgjlltn9">
                  We use your answers to support your X1 and improve our products and advertising. We never sell your data.{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-steel-300" data-id="element-1661" data-mp="b3jofz4d5pa9">Privacy policy</Link>
                </p>
              </div>
            </form>}
        </div>
      </main>
      <SiteFooter data-id="element-1662" data-mp="3eip3szgr53jd" />
    </div>;
}